import { useState } from 'react'
import styled from '@emotion/styled'
import { keyframes } from '@emotion/react'
import CloseButton from './CloseButton'
import Backdrop from './Backdrop'
import useEscModalHandler from 'shop/hooks/useEscModalHandler'
import { StyledHTMLElement } from 'shop/theme/types'

interface Props {
  children: React.ReactNode
  top: string
  handleCloseModal: () => void
}

const SideModal = ({ children, handleCloseModal, top }: Props) => {
  const [isClosing, setIsClosing] = useState(false)

  /** Handles animation onClose / calls close modal func after animation */
  const onClose = () => {
    setIsClosing(true)
    // Wait for animation to complete before actually closing
    setTimeout(() => {
      handleCloseModal()
    }, 200)
  }

  useEscModalHandler({ onClose })

  return (
    <ModalContainer data-testid='sideModal' top={top}>
      <Dialog isClosing={isClosing}>
        <CloseButton variant={'sideModal'} handleCloseModal={onClose} />
        {children}
      </Dialog>
      <Backdrop onClick={onClose} />
    </ModalContainer>
  )
}

const ModalContainer = styled.div<StyledHTMLElement & { top: string }>(
  ({ theme, top }) => ({
    position: 'fixed',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    zIndex: 30,
    display: 'flex',
    [theme.mediaQueries.viewport7]: {
      top: top
    }
  })
)

const Dialog = styled.div<{ isClosing: boolean }>(({ theme, isClosing }) => ({
  overflowY: 'auto',
  overflowX: 'hidden',
  backgroundColor: 'white',
  height: '100%',
  maxWidth: '576px',
  width: '100%',
  position: 'absolute',
  display: 'flex',
  flexDirection: 'column',
  top: 0,
  right: 0,
  zIndex: theme.zIndex.genericModal,
  padding: '30px 16px 0px',
  animation: isClosing
    ? `${slideDownOut} 200ms ease-in forwards`
    : `${slideUp} 200ms ease-out`,
  '> h1': {
    marginBottom: '36px'
  },
  [theme.mediaQueries.viewport5]: {
    padding: '24px 24px 0px'
  },
  [theme.mediaQueries.viewport7]: {
    animation: isClosing
      ? `${slideRightOut} 200ms ease-in forwards`
      : `${slideRight} 200ms ease-out`,
    padding: '32px 16px 0px'
  }
}))

const slideRight = keyframes`
  0% {
    opacity: 0;
    transform: translateX(560px);
  }

  100% {
    opacity: 1;
    transform: translateX(0);
  }
`

const slideUp = keyframes`
  0% {
    opacity: 0;
    transform: translateY(360px);
  }

  100% {
    opacity: 1;
    transform: translateY(0);
  }
`

const slideRightOut = keyframes`
  0% {
    opacity: 1;
    transform: translateX(0);
  }

  100% {
    opacity: 0;
    transform: translateX(560px);
  }
`

const slideDownOut = keyframes`
  0% {
    opacity: 1;
    transform: translateY(0);
  }

  100% {
    opacity: 0;
    transform: translateY(360px);
  }
`

export default SideModal
